import React, { useState, ChangeEvent, useMemo, useCallback, FormEvent, JSX } from 'react';
import { Button, Col, Input, Label, Row } from 'reactstrap';
import { faGlobe } from '@fortawesome/free-solid-svg-icons/faGlobe';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons/faExclamationTriangle';
import Keys from '../Translations/generated/search-content.de.json.keys';
import fallback from '../Translations/generated/search-content.de.json';
import { useTranslation } from '@ibe/components';
import useSearchApi from '../Util/useSearchApi';
import { ApiProductCard, ApiProductSearchRequestFromJSON } from '../../api';
import { ApiGetPackagesResponse } from '@ibe/api';
import { LoggerFactory, SessionStorage } from '@ibe/services';
import { SessionStorageType } from './Search';
import { productSearchSessionKey } from '../Config/sessionkeys';
import { useMount } from 'react-use';

const logger = LoggerFactory.get('SearchForm');

export type FormType = 'PRODUCT' | 'PACKAGE';
export type ListType = (ApiGetPackagesResponse | null) | ApiProductCard[];
export type OnPackagesLoaded = (search: string, list: ListType, formType?: FormType) => void;

const ProductsSearchForm = ({
  search,
  onPackagesLoaded,
  inputId = 'product-search-input',
  updateUrlParam = true
}: {
  search?: string;
  onPackagesLoaded: OnPackagesLoaded;
  inputId?: string;
  updateUrlParam?: boolean;
}): JSX.Element => {
  const { t } = useTranslation('search-content', fallback);
  const searchApi = useSearchApi();
  const [noResults, setNoResults] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [value, setValue] = useState<string>(search ?? '');

  function getSearchParams() {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const searchTerm = urlParams.get('searchTerm');
    if (searchTerm) {
      const session = sessionStorage.get();
      if (
        !!session &&
        session.search === searchTerm &&
        Array.isArray(session.list) &&
        session.list.length > 0
      ) {
        setValue(searchTerm);
        onPackagesLoaded(searchTerm, session.list);
      } else {
        performSearch(searchTerm).then();
      }
    }
  }

  function updateSearchParams(searchTerm: string) {
    if (!updateUrlParam) {
      return;
    }
    const updatedParams = new URLSearchParams(globalThis.location.search);
    if (updatedParams.get('searchTerm') === searchTerm) {
      return;
    }
    updatedParams.set('searchTerm', searchTerm);
    const newUrl = `${globalThis.location.pathname}?${updatedParams.toString()}`;
    globalThis.history.pushState(null, '', newUrl);
  }

  function clearSearchParams() {
    if (!updateUrlParam) {
      return;
    }
    const updatedParams = new URLSearchParams(globalThis.location.search);
    if (!updatedParams.has('searchTerm')) {
      return;
    }
    updatedParams.delete('searchTerm');
    const query = updatedParams.toString();
    const queryParam = query ? `?${query}` : '';
    globalThis.history.replaceState(null, '', `${globalThis.location.pathname}${queryParam}`);
  }

  useMount(() => {
    getSearchParams();
  });

  const sessionStorage = new SessionStorage<SessionStorageType>(productSearchSessionKey);

  const isValid = useMemo(() => {
    return value?.length > 2;
  }, [value]);

  const performSearch = async (searchTerm: string) => {
    setValue(searchTerm);
    updateSearchParams(searchTerm);
    try {
      const result = await searchApi.searchProducts(
        ApiProductSearchRequestFromJSON({ searchTerm: searchTerm })
      );
      if (!!result?.items && result?.items?.length > 0) {
        onPackagesLoaded(searchTerm, result.items);
        sessionStorage.set({ search: searchTerm, list: result.items });
      } else {
        sessionStorage.set({ search: searchTerm, list: [] });
        onPackagesLoaded(searchTerm, null);
        setNoResults(true);
      }
    } catch (err) {
      setError(true);
      logger.error(err);
    }
  };

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();
      setNoResults(false);
      setError(false);
      if (isValid) {
        await performSearch(value);
      }
    },
    [value, isValid, onPackagesLoaded]
  );

  return (
    <div className="thg-search">
      <form className="search-form" aria-label={t(Keys.searchFormLabel)} onSubmit={handleSubmit}>
        <Row>
          <Col sm={8} className="mb-4 mb-sm-0">
            <Label className="search-form__label font-weight-medium" htmlFor={inputId}>
              {t(Keys.destinationsLabel)}
            </Label>
            <div className="position-relative">
              <GlobeIcon className={'svg-inline--fa fa-globe search-form__icon'} />
              <Input
                className="search-form__input"
                id={inputId}
                value={value}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setValue(e.target.value);
                  if (e.target.value === '') {
                    clearSearchParams();
                  }
                }}
                placeholder={t(Keys.destinationsPlaceholder)}
              />
            </div>
          </Col>
          <Col sm={4} className="d-flex align-items-end">
            <Button
              color="primary"
              type="submit"
              className="search-form__submit"
              disabled={!isValid}
            >
              {t(Keys.search)}
            </Button>
          </Col>
        </Row>
        {(noResults || error) && (
          <Row className="mt-4">
            <Col>
              {noResults && <SearchWarning message={t(Keys.noResultsFound)} />}
              {error && <SearchWarning message={t(Keys.searchError)} />}
            </Col>
          </Row>
        )}
      </form>
    </div>
  );
};

function SearchWarning(props: { readonly message: string }): React.ReactElement {
  return (
    <div className="alert alert-warning" role="alert">
      <ExclamationIcon className="svg-inline--fa fa-triangle-exclamation mr-2" />
      <span>{props.message}</span>
    </div>
  );
}

function ExclamationIcon(props: Readonly<React.SVGProps<SVGSVGElement>>) {
  /* eslint-disable*/
  const [w, h, , , d] = faExclamationTriangle.icon as [
    number,
    number,
    any,
    string,
    string | string[]
  ];
  const path = Array.isArray(d) ? d.join(' ') : d;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={16} height={16} aria-hidden="true" {...props}>
      <path d={path} fill="currentColor" />
    </svg>
  );
}

function GlobeIcon(props: React.SVGProps<SVGSVGElement>) {
  /* eslint-disable-next-line */
  const [w, h, , , d] = faGlobe.icon as [number, number, any, string, string | string[]];
  const path = Array.isArray(d) ? d.join(' ') : d;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} height="1em" aria-hidden="true" {...props}>
      <path d={path} fill="currentColor" />
    </svg>
  );
}

export default ProductsSearchForm;
